/**
 * RestoID — Forgot password (request reset code).
 * ---------------------------------------------------------------------------
 * Phone-first RestoID presentation on the PRISTINE upstream forgot-password
 * flow (`useSendVerificationCode(UserFlow.ForgotPassword)`). Falls back to the
 * upstream identifier form when phone is not an enabled reset method.
 * Guard mirrors the upstream page. Swapped in via `src/App.tsx`.
 */
import { SignInIdentifier } from '@logto/schemas';
import { useCallback, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';

import FirstScreenLayout from '@/Layout/FirstScreenLayout';
import UserInteractionContext from '@/Providers/UserInteractionContextProvider/UserInteractionContext';
import CaptchaBox from '@/containers/CaptchaBox';
import useNavigateWithPreservedSearchParams from '@/hooks/use-navigate-with-preserved-search-params';
import usePrefilledIdentifier from '@/hooks/use-prefilled-identifier';
import useSendVerificationCode from '@/hooks/use-send-verification-code';
import { useForgotPasswordSettings } from '@/hooks/use-sie';
import ForgotPasswordForm from '@/pages/ForgotPassword/ForgotPasswordForm';
import { UserFlow, type VerificationCodeIdentifier } from '@/types';
import { identifierInputDescriptionMap } from '@/utils/form';

import { Button, C, FONT, PhoneInput, ScreenHead, Stack } from '../components/restoid';
import { useCopy } from '../copy';

import ErrorPage from './ErrorPage';
import { useIdentifierValue } from './IdentifierSignIn';

/** Phone-first reset-code request form, driven by the upstream send-code flow. */
const PhoneForgotForm = ({ enabledMethods }: { enabledMethods: VerificationCodeIdentifier[] }) => {
  const c = useCopy();
  const { errorMessage, clearErrorMessage, onSubmit } = useSendVerificationCode(
    UserFlow.ForgotPassword
  );
  const { setForgotPasswordIdentifierInputValue } = useContext(UserInteractionContext);

  /**
   * Pre-filled exactly as upstream's page pre-fills it: the identifier cached on
   * `UserInteractionContext` by `ForgotPasswordLink` (or by a magic-link verify),
   * else the `login_hint` search parameter.
   */
  const {
    value: phone,
    setValue: setPhone,
    isFilled: valid,
    submitValue,
  } = useIdentifierValue(SignInIdentifier.Phone, enabledMethods, { isForgotPassword: true });
  const [submitting, setSubmitting] = useState(false);

  const submit = useCallback(async () => {
    if (!valid || submitting) {
      return;
    }
    clearErrorMessage();
    setForgotPasswordIdentifierInputValue({ type: SignInIdentifier.Phone, value: submitValue });
    setSubmitting(true);
    try {
      await onSubmit({ identifier: SignInIdentifier.Phone, value: submitValue });
    } finally {
      setSubmitting(false);
    }
  }, [
    valid,
    submitting,
    clearErrorMessage,
    submitValue,
    setForgotPasswordIdentifierInputValue,
    onSubmit,
  ]);

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        void submit();
      }}
    >
      <Stack gap={22}>
        <div>
          <label
            style={{ display: 'block', color: C.sub, font: `500 13px/1 ${FONT}`, marginBottom: 9 }}
          >
            {c('phoneLabel')}
          </label>
          <PhoneInput value={phone} onChange={setPhone} danger={Boolean(errorMessage)} />
          {errorMessage ? (
            <div
              role="alert"
              style={{ marginTop: 8, color: C.danger, font: `400 12.5px/1.45 ${FONT}` }}
            >
              {errorMessage}
            </div>
          ) : null}
        </div>
        <CaptchaBox />
        <Button
          htmlType="submit"
          title={c('getCode')}
          isLoading={submitting}
          isDisabled={!valid}
          onClick={() => void submit()}
        />
      </Stack>
    </form>
  );
};

const ForgotPassword = () => {
  const { t } = useTranslation();
  const c = useCopy();
  const navigate = useNavigateWithPreservedSearchParams();
  const { isForgotPasswordEnabled, enabledMethodSet } = useForgotPasswordSettings();
  const enabledMethods = [...enabledMethodSet];
  // Upstream's page pre-fills its form the same way; the fallback keeps that.
  const { value: prefilledValue } = usePrefilledIdentifier({
    enabledIdentifiers: enabledMethods,
    isForgotPassword: true,
  });

  if (!isForgotPasswordEnabled) {
    return <ErrorPage />;
  }

  const hasPhone = enabledMethodSet.has(SignInIdentifier.Phone);

  return (
    <FirstScreenLayout pageMeta={{ titleKey: 'description.reset_password' }}>
      <Stack gap={22}>
        <ScreenHead
          back={() => navigate(-1)}
          backLabel={c('backShort')}
          title={t('description.reset_password')}
          sub={t('description.reset_password_description', {
            types: enabledMethods.map((method) => t(identifierInputDescriptionMap[method])),
          })}
        />
        {hasPhone ? (
          <PhoneForgotForm enabledMethods={enabledMethods} />
        ) : (
          <ForgotPasswordForm
            autoFocus
            defaultValue={prefilledValue}
            enabledTypes={enabledMethods}
          />
        )}
      </Stack>
    </FirstScreenLayout>
  );
};

export default ForgotPassword;
